
import React, { useEffect, useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Navigate } from 'react-router-dom';
import { useAuthSession } from '@/hooks/useAuthSession';
import { useAdmin } from '@/hooks/useAdmin';
import { 
  isInSubscriptionGracePeriod, 
  clearSubscriptionGracePeriod, 
  isInPostExpiryGracePeriod,
  getPostExpiryGraceHoursRemaining
} from '@/utils/SubscriptionRedirectHelper';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Loader2 } from 'lucide-react';
import { checkFreePlanExpiry } from '@/utils/freePlanHelpers';
import { GracePeriodBanner } from './GracePeriodBanner';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  requiredFeature?: string;
}

export const SubscriptionGuard = ({
  children,
  requiredFeature
}: SubscriptionGuardProps) => {
  const { subscription, isLoading, refreshSubscription, subscriptionPlans } = useSubscription();
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { isAuthenticated, session } = useAuthSession();
  const [checkComplete, setCheckComplete] = useState(false);
  const [gracePeriodUsed, setGracePeriodUsed] = useLocalStorage('grace_period_used', false);
  const [freePlanExpired, setFreePlanExpired] = useState(false);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [graceHoursRemaining, setGraceHoursRemaining] = useState(0);
  const [gracePeriodType, setGracePeriodType] = useState<string | null>(null);

  const isDirectAdmin = session?.user?.email === 'admin@admin.com';
  const hasActiveSubscription = subscription?.has_active_subscription || false;
  const currentPlan = subscription?.subscription?.plan?.toLowerCase() || '';
  const isTrial = subscription?.subscription?.is_trial || false;
  const isFreePlan = currentPlan === 'free' || currentPlan === 'starter';

  // Get feature list from subscription directly
  const planFeatures = subscription?.subscription?.features || [];

  // Check if subscription is in grace period
  useEffect(() => {
    // Check regular subscription grace period (after activating a new subscription)
    const inRegularGracePeriod = isInSubscriptionGracePeriod();
    
    // Check if any plan type is in post-expiry grace period
    const planTypes = ['free', 'trial', 'paid'];
    let foundGracePeriod = false;
    let foundType = null;
    let remainingHours = 0;
    
    for (const planType of planTypes) {
      if (isInPostExpiryGracePeriod(planType)) {
        foundGracePeriod = true;
        foundType = planType;
        remainingHours = getPostExpiryGraceHoursRemaining(planType);
        break;
      }
    }
    
    setInGracePeriod(inRegularGracePeriod || foundGracePeriod);
    setGracePeriodType(foundGracePeriod ? foundType : (inRegularGracePeriod ? 'regular' : null));
    setGraceHoursRemaining(remainingHours);
  }, []);

  // Check if Free plan expired
  useEffect(() => {
    const checkFreeExpiry = async () => {
      if (hasActiveSubscription && isFreePlan) {
        const expiryStatus = await checkFreePlanExpiry();
        setFreePlanExpired(expiryStatus?.expired || false);
      }
    };
    checkFreeExpiry();
  }, [hasActiveSubscription, isFreePlan]);

  // Refresh subscription if in grace period
  const isInGracePeriod = isInSubscriptionGracePeriod();
  useEffect(() => {
    const handleGracePeriod = async () => {
      if (isInGracePeriod && !gracePeriodUsed) {
        await refreshSubscription();
        setGracePeriodUsed(true);
      }
    };
    handleGracePeriod();
  }, [isInGracePeriod, refreshSubscription, gracePeriodUsed, setGracePeriodUsed]);

  // Final access check
  const hasAccessToFeature = () => {
    if (!requiredFeature) return true;

    // Admins or trials get everything
    if (isAdmin || isDirectAdmin || isTrial) return true;

    // Allow access if in any grace period
    if (inGracePeriod) return true;

    // Check if feature exists in user's subscription
    return Array.isArray(planFeatures) && planFeatures.includes(requiredFeature);
  };

  useEffect(() => {
    if (!isLoading && !isAdminLoading) {
      setCheckComplete(true);
      if ((hasActiveSubscription || isAdmin || isDirectAdmin || isTrial) && isInGracePeriod) {
        clearSubscriptionGracePeriod();
      }
    }
  }, [isLoading, isAdminLoading, hasActiveSubscription, isAdmin, isDirectAdmin, isTrial, isInGracePeriod]);

  if (!checkComplete) {
    return (
      <div className="w-full h-24 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="h-6 w-4 animate-spin text-primary mb-2" />
          <p className="text-sm text-muted-foreground">Verifying subscription...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!hasAccessToFeature()) {
    if (freePlanExpired) {
      return <Navigate to="/subscription/plans?expired=free" replace />;
    }
    return <Navigate to="/subscription/plans" replace />;
  }

  return (
    <>
      {inGracePeriod && gracePeriodType && gracePeriodType !== 'regular' && (
        <GracePeriodBanner 
          planType={gracePeriodType}
          hoursRemaining={graceHoursRemaining}
        />
      )}
      {children}
    </>
  );
};
